import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { PageViewTracker } from './components/PageViewTracker';
import { LandingPage } from './pages/Landing/LandingPage';
import PlanWizard from './components/PlanWizard/PlanWizard';
import { ToolsHub } from './pages/Tools/ToolsHub';
import { ToolPlaceholder } from './pages/Tools/ToolPlaceholder';
import { getToolBySlug } from './pages/Tools/toolsData';
import { MobileAwareLayout } from './components/MobileAwareLayout';
import { MobileLayout } from './components/MobileLayout/MobileLayout';
import { useIsMobile } from './hooks/useIsMobile';
import { initPostHog, captureUtmParams, trackToolOpened } from './services/analytics';
import './styles/App.css';
import './styles/MobileContent.css';

// Initialise analytics (no-op if VITE_POSTHOG_KEY is not set)
initPostHog();
captureUtmParams();

// Lazy-loaded pages — only fetched when the route is visited
const ProjectionPage = lazy(() => import('./pages/Projection/ProjectionPage').then(m => ({ default: m.ProjectionPage })));
const ProjectionPageMobile = lazy(() => import('./pages/Projection/ProjectionPage.mobile').then(m => ({ default: m.ProjectionPageMobile })));
const ComparisonPage = lazy(() => import('./pages/Comparison/ComparisonPage').then(m => ({ default: m.ComparisonPage })));
const ComparisonPageMobile = lazy(() => import('./pages/Comparison/ComparisonPage.mobile').then(m => ({ default: m.ComparisonPageMobile })));
const LoginPage = lazy(() => import('./pages/Auth/LoginPage').then(m => ({ default: m.LoginPage })));
const LoginPageMobile = lazy(() => import('./pages/Auth/LoginPage.mobile').then(m => ({ default: m.default })));
const RegisterPage = lazy(() => import('./pages/Auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const RegisterPageMobile = lazy(() => import('./pages/Auth/RegisterPage.mobile').then(m => ({ default: m.default })));
const VerifyEmailPage = lazy(() => import('./pages/Auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const VerifyEmailPageMobile = lazy(() => import('./pages/Auth/VerifyEmailPage.mobile').then(m => ({ default: m.default })));
const CheckEmailPage = lazy(() => import('./pages/Auth/CheckEmailPage').then(m => ({ default: m.CheckEmailPage })));
const CheckEmailPageMobile = lazy(() => import('./pages/Auth/CheckEmailPage.mobile').then(m => ({ default: m.default })));
const ForgotPasswordPage = lazy(() => import('./pages/Auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ForgotPasswordPageMobile = lazy(() => import('./pages/Auth/ForgotPasswordPage.mobile').then(m => ({ default: m.default })));
const ResetPasswordPage = lazy(() => import('./pages/Auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const ResetPasswordPageMobile = lazy(() => import('./pages/Auth/ResetPasswordPage.mobile').then(m => ({ default: m.default })));
const PricingPage = lazy(() => import('./pages/Pricing/PricingPage').then(m => ({ default: m.PricingPage })));
const AdminPage = lazy(() => import('./pages/Admin/AdminPage').then(m => ({ default: m.AdminPage })));
const ApiKeysPage = lazy(() => import('./pages/ApiKeys/ApiKeysPage').then(m => ({ default: m.ApiKeysPage })));
const ApiDocsPage = lazy(() => import('./pages/ApiKeys/ApiDocsPage').then(m => ({ default: m.ApiDocsPage })));
const PrivacyPolicy = lazy(() => import('./pages/Legal/PrivacyPolicy').then(m => ({ default: m.PrivacyPolicy })));
const TermsOfService = lazy(() => import('./pages/Legal/TermsOfService').then(m => ({ default: m.TermsOfService })));
const AccountPage = lazy(() => import('./pages/Account/AccountPage').then(m => ({ default: m.AccountPage })));

// Shared loading fallbacks — styled via App.css (always loaded)
const authFallback = <div className="app-loading app-loading--fullscreen"><div className="app-loading-spinner" /></div>;
const pageFallback = <div className="app-loading"><div className="app-loading-spinner" /></div>;

// Tool pages — lazy-loaded per slug
const lazyToolLoaders: Record<string, () => Promise<{ default: React.ComponentType }>> = {
  compounding: () => import('./pages/Compounding/CompoundingPage').then(m => ({ default: m.CompoundingPage })),
  'salary-sacrifice': () => import('./pages/SalarySacrifice/SalarySacrificePage').then(m => ({ default: m.SalarySacrificePage })),
  'coast-fire': () => import('./pages/CoastFire/CoastFirePage').then(m => ({ default: m.CoastFirePage })),
  'retirement-mot': () => import('./pages/RetirementMOT/RetirementMOTPage').then(m => ({ default: m.RetirementMOTPage })),
  'employer-match': () => import('./pages/EmployerMatch/EmployerMatchPage').then(m => ({ default: m.EmployerMatchPage })),
  volatility: () => import('./pages/Volatility/VolatilityPage').then(m => ({ default: m.VolatilityPage })),
  'pension-gap': () => import('./pages/PensionGap/PensionGapPage').then(m => ({ default: m.PensionGapPage })),
  'earliest-retirement': () => import('./pages/EarliestRetirement/EarliestRetirementPage').then(m => ({ default: m.EarliestRetirementPage })),
  'ni-top-up': () => import('./pages/NiTopUp/NiTopUpPage').then(m => ({ default: m.NiTopUpPage })),
};

// Cache lazy components so React.lazy is only called once per slug
const lazyToolCache: Record<string, React.LazyExoticComponent<React.ComponentType>> = {};

function getLazyTool(slug: string): React.LazyExoticComponent<React.ComponentType> | undefined {
  const loader = lazyToolLoaders[slug];
  if (!loader) return undefined;
  if (!lazyToolCache[slug]) {
    lazyToolCache[slug] = lazy(loader);
  }
  return lazyToolCache[slug];
}

function LandingWrapper({ children }: { children: React.ReactNode }) {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileLayout>{children}</MobileLayout>;
  return <>{children}</>;
}

function ToolRoute() {
  const { slug } = useParams<{ slug: string }>();
  const tool = slug ? getToolBySlug(slug) : undefined;

  useEffect(() => {
    if (tool?.status === 'live' && tool.slug) trackToolOpened(tool.slug);
  }, [tool?.slug, tool?.status]);

  if (!tool) {
    return <Navigate to="/tools" replace />;
  }

  if (tool.status === 'live') {
    const LazyToolComponent = getLazyTool(tool.slug);
    if (!LazyToolComponent) {
      console.error(
        `[ToolRoute] Tool "${tool.slug}" is marked as live but has no entry in lazyToolLoaders.`,
      );
    } else {
      return (
        <Suspense fallback={pageFallback}>
          <LazyToolComponent />
        </Suspense>
      );
    }
  }

  return <ToolPlaceholder tool={tool} />;
}

/** Renders mobile or desktop auth component based on useIsMobile */
function AuthRoute({ desktop: D, mobile: M }: { desktop: React.ComponentType; mobile: React.ComponentType }) {
  const isMobile = useIsMobile();
  const Component = isMobile ? M : D;
  return <Component />;
}

function App() {
  return (
    <BrowserRouter>
      <PageViewTracker />
      <AuthProvider>
        <RouteErrorBoundary>
          <Routes>
            <Route path="/login" element={<Suspense fallback={authFallback}><AuthRoute desktop={LoginPage} mobile={LoginPageMobile} /></Suspense>} />
            <Route path="/register" element={<Suspense fallback={authFallback}><AuthRoute desktop={RegisterPage} mobile={RegisterPageMobile} /></Suspense>} />
            <Route path="/verify-email" element={<Suspense fallback={authFallback}><AuthRoute desktop={VerifyEmailPage} mobile={VerifyEmailPageMobile} /></Suspense>} />
            <Route path="/check-email" element={<Suspense fallback={authFallback}><AuthRoute desktop={CheckEmailPage} mobile={CheckEmailPageMobile} /></Suspense>} />
            <Route path="/forgot-password" element={<Suspense fallback={authFallback}><AuthRoute desktop={ForgotPasswordPage} mobile={ForgotPasswordPageMobile} /></Suspense>} />
            <Route path="/reset-password" element={<Suspense fallback={authFallback}><AuthRoute desktop={ResetPasswordPage} mobile={ResetPasswordPageMobile} /></Suspense>} />
            <Route path="/admin" element={<Suspense fallback={authFallback}><AdminPage /></Suspense>} />
            <Route path="/pricing" element={<Suspense fallback={authFallback}><MobileAwareLayout><PricingPage /></MobileAwareLayout></Suspense>} />
            <Route path="/privacy" element={<Suspense fallback={authFallback}><MobileAwareLayout><PrivacyPolicy /></MobileAwareLayout></Suspense>} />
            <Route path="/terms" element={<Suspense fallback={authFallback}><MobileAwareLayout><TermsOfService /></MobileAwareLayout></Suspense>} />
            <Route
              path="/tools"
              element={
                <ProtectedRoute>
                  <MobileAwareLayout>
                    <ToolsHub />
                  </MobileAwareLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools/:slug"
              element={
                <ProtectedRoute>
                  <MobileAwareLayout>
                    <ToolRoute />
                  </MobileAwareLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/account/api-keys"
              element={
                <ProtectedRoute>
                  <MobileAwareLayout>
                    <Suspense fallback={pageFallback}>
                      <ApiKeysPage />
                    </Suspense>
                  </MobileAwareLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute>
                  <MobileAwareLayout>
                    <Suspense fallback={pageFallback}>
                      <AccountPage />
                    </Suspense>
                  </MobileAwareLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/api-docs"
              element={
                <ProtectedRoute>
                  <MobileAwareLayout>
                    <Suspense fallback={pageFallback}>
                      <ApiDocsPage />
                    </Suspense>
                  </MobileAwareLayout>
                </ProtectedRoute>
              }
            />
            <Route path="/" element={
              <LandingWrapper>
                <LandingPage />
              </LandingWrapper>
            } />
            <Route
              path="/plan"
              element={
                <ProtectedRoute>
                  <MobileAwareLayout mainClass="app-main--with-sidebar">
                    <PlanWizard />
                  </MobileAwareLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projections/compare"
              element={
                <ProtectedRoute>
                  <MobileAwareLayout mainClass="app-main--with-sidebar">
                    <Suspense fallback={pageFallback}>
                      <AuthRoute desktop={ComparisonPage} mobile={ComparisonPageMobile} />
                    </Suspense>
                  </MobileAwareLayout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/plans/:planId/projection"
              element={
                <ProtectedRoute>
                  <MobileAwareLayout mainClass="app-main--with-sidebar">
                    <Suspense fallback={pageFallback}>
                      <AuthRoute desktop={ProjectionPage} mobile={ProjectionPageMobile} />
                    </Suspense>
                  </MobileAwareLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </RouteErrorBoundary>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
